import { useMemo } from 'react';
import { getLogger } from '@sas/nova-core/logging';
import { useQuery } from '@tanstack/react-query';
import { getMixedLocations } from '../../services/__internal__/common';
import { getContentDataProvider } from '../../services/__internal__/ContentDataProvider';
const logger = getLogger('useContentQuery');

/**
 * Helper function for using useQuery in the style required by the content components.
 * @param config.queryFn The API function that will actually be called to get the data. A runtime error
 * will occur if it doesn't match one of the known and supported query fns specified in ContentQueryId
 * @param config.dataProvider The ContentDataProvider to be used with this query.
 * @param config.queryParams The params that will be passed into queryFn. Important note: since there
 * are valid cases where params may not be resolved by first render, if these are undefined it will
 * disable the query
 * @param rqConfig
 * @param debugMsg Optional debug message that can be passed in, to aid in figuring out what triggered
 * a useContentQuery
 */
export function useContentQuery(config, rqConfig, debugMsg) {
  // TODO: The bulk of the code below ( that which is related to the query argument generation ) was extracted
  // into a separate useContentQueryArgs hook where query arguments would be generated for both useQuery and
  // fetchQuery contexts.  However, this (separate hook) version is only used for fetchQuery contexts currently
  // as there were issues when refactoring useContentQuery to use this separate hook for argument generation.
  // Issues ( in form of ts compile errors ) were related to it no longer being determinable whether
  // useContentQuery was returning expected form of result ( e.g. Item or Item[] ).  Suspect there is
  // way to resolve the compile errors but ROI of time for working on this further was not justifiable
  // based on other priorities.
  //
  // In the meantime, changes made below would likely also be preferably made in useContentQueryArgs.
  //
  // const {
  //    queryKey,
  //    queryFn,
  //    sanitizedQueryParams,
  //    anyRqConfig,
  // } = useContentQueryArgs(config, rqConfig, debugMsg);
  const defaultDataProvider = getContentDataProvider();
  let queryId;
  let queryTypeConfig;
  let dataProviderInfo;
  const getRelevantDataProvider = getRelevantDataProviderFn(config.dataProvider, defaultDataProvider);
  const sanitizedQueryParams = useMemo(() => sanitizeParamsForQueryKey(config.queryFn, config.queryParams), [config.queryFn, config.queryParams]);
  if (config.queryFn === config.dataProvider.getChildrenByIdentifier) {
    queryId = `getChildrenByIdentifier`;
    queryTypeConfig = sanitizedQueryParams ? {
      type: 'itemsContainer',
      containerIdentifier: sanitizedQueryParams[0]
    } : undefined;
    const relevantDataProvider = getRelevantDataProvider('getChildrenByIdentifier');
    dataProviderInfo = {
      type: 'single',
      value: {
        name: relevantDataProvider.name,
        itemProvider: relevantDataProvider.itemProvider
      }
    };
  } else if (config.queryFn === config.dataProvider.getItemByIdentifier) {
    queryId = `getItemByIdentifier`;
    queryTypeConfig = sanitizedQueryParams ? {
      type: 'itemStatic'
    } : undefined;
    const relevantDataProvider = getRelevantDataProvider('getItemByIdentifier');
    dataProviderInfo = {
      type: 'single',
      value: {
        name: relevantDataProvider.name,
        itemProvider: relevantDataProvider.itemProvider
      }
    };
  } else if (config.queryFn === config.dataProvider.getItemsByIdentifier) {
    queryId = `getItemsByIdentifier`;
    queryTypeConfig = sanitizedQueryParams ? {
      type: 'itemStatic'
    } : undefined;
    const relevantDataProvider = getRelevantDataProvider('getItemsByIdentifier');
    dataProviderInfo = {
      type: 'single',
      value: {
        name: relevantDataProvider.name,
        itemProvider: relevantDataProvider.itemProvider
      }
    };
  } else if (config.queryFn === config.dataProvider.getFolderChildrenByIdentifier) {
    queryId = `getFolderChildrenByIdentifier`;
    queryTypeConfig = sanitizedQueryParams ? {
      type: 'itemsContainer',
      containerIdentifier: sanitizedQueryParams[0]
    } : undefined;
    const relevantDataProvider = getRelevantDataProvider('getFolderChildrenByIdentifier');
    dataProviderInfo = {
      type: 'single',
      value: {
        name: relevantDataProvider.name,
        itemProvider: relevantDataProvider.itemProvider
      }
    };
  } else if (config.queryFn === config.dataProvider.getAncestryByIdentifier) {
    queryId = `getAncestryByIdentifier`;
    queryTypeConfig = sanitizedQueryParams ? {
      type: 'ancestorsContainer',
      containerIdentifier: sanitizedQueryParams[0]
    } : undefined;
    const relevantDataProvider = getRelevantDataProvider('getAncestryByIdentifier');
    dataProviderInfo = {
      type: 'single',
      value: {
        name: relevantDataProvider.name,
        itemProvider: relevantDataProvider.itemProvider
      }
    };
  } else if (config.queryFn === config.dataProvider.getPreviousParent) {
    queryId = `getPreviousParent`;
    queryTypeConfig = sanitizedQueryParams ? {
      type: 'itemStatic'
    } : undefined;
    const relevantDataProvider = getRelevantDataProvider('getPreviousParent');
    dataProviderInfo = {
      type: 'single',
      value: {
        name: relevantDataProvider.name,
        itemProvider: relevantDataProvider.itemProvider
      }
    };
  } else if (config.queryFn === getMixedLocations) {
    if (config.queryParams) {
      queryId = 'getMixedLocations';
      queryTypeConfig = sanitizedQueryParams ? {
        type: 'itemsStatic'
      } : undefined;
      const identifiers = config.queryParams[0];
      const paramDataProvider = config.queryParams[1] ?? getContentDataProvider();
      const dpInfoValue = identifiers.map(identifier => {
        // For each identifier, create a dataProviderInfo value entry using either the dp from the
        // identifier, or the dp from the params
        const relevantDataProvider = identifier.dataProvider ?? paramDataProvider;
        return {
          name: relevantDataProvider.name,
          itemProvider: relevantDataProvider.itemProvider
        };
      });
      dataProviderInfo = {
        type: 'mixed',
        value: dpInfoValue
      };
    } else {
      queryId = 'getMixedLocations(disabled)';
      dataProviderInfo = {
        type: 'mixed',
        value: []
      };
    }
  } else {
    throw new Error('useContentQuery called with an unexpected query function');
  }

  /*
  The rqConfig required by useQuery does a lot of generic jiu-jitsu to make sure things like the queryFn
  return data and query key type pass through to certain functional variants of certain props. I
  found this too difficult to reconstruct on our independent rqConfig prop. Casting to any
  side-steps the problem. We'll miss full type-checking on those (hopefully) rare functional
  property options, but, consumers still get general typechecking for the rqConfig options, so that
  seems good enough until experience proves otherwise.
  */
  const anyRqConfig = rqConfig;
  const queryFn = _context => {
    if (!sanitizedQueryParams) {
      // Enabled guard should prevent this from ever actually being hit
      throw new Error('useContentQuery queryFn unexpectedly called without parameters');
    }
    if (debugMsg) {
      logger.debug(`logging debugMsg: ${debugMsg}\nqueryKey: ${JSON.stringify(queryKey, undefined, 2)}`);
    }
    return config.queryFn(...config.queryParams);
  };
  const queryKey = {
    isContentQueryKey: true,
    queryId,
    queryTypeConfig,
    queryParams: sanitizedQueryParams,
    dataProviderInfo
  };
  return useQuery([queryKey], queryFn,
  // () => {
  //   if (!sanitizedQueryParams) {
  //     // TODO: more proper error handling
  //     // Enabled guard should prevent this from every actually being hit
  //     throw new Error('useContentQuery queryFn unexpectedly called without parameters');
  //   }

  //   return config.queryFn(...(config.queryParams as any[]));
  // },
  {
    refetchOnWindowFocus: false,
    enabled: !!sanitizedQueryParams,
    // Default staleTime of 1m
    staleTime: 1 * 60 * 1000,
    ...anyRqConfig
  });
}

/**
 * A type guard to make typed handling of useContentQuery keys easier elsewhere
 * @param obj
 * @returns
 */
export const isContentQuerykey = obj => {
  return obj.isContentQueryKey;
};

// Function that will return a function that checks whether to use the supplied or default data
// provider for a particular API call. Detects whether the default function was actually overridden.
export const getRelevantDataProviderFn = (dataProvider, defaultDataProvider) => {
  // If the queryFn on the custom DP is strictly equal to the one from the default DP, we can
  // assume the consumer did not override that particular function. In this case, we should use
  // the default DP to avoid splitting the cache.
  return queryFnName => {
    return dataProvider[queryFnName] === defaultDataProvider[queryFnName] ? defaultDataProvider : dataProvider;
  };
};

// We don't want to serialize the actual data providers on any of the params, so this will remove any
// occurrences of data providers from ItemIdentifiers and from the special getMixedLocations case
export const sanitizeParamsForQueryKey = (queryFn, params) => {
  if (!params) {
    return params;
  }
  let anyParams = params;

  // Remove second dataProvider arg from getMixedLocations params
  if (queryFn === getMixedLocations) {
    anyParams = [anyParams[0]];
  }
  const sanitizedParams = [];
  for (const param of anyParams) {
    if (Array.isArray(param)) {
      sanitizedParams.push(param.map(subParam => sanitizeParamForQueryKey(subParam)));
    } else {
      sanitizedParams.push(sanitizeParamForQueryKey(param));
    }
  }
  return sanitizedParams;
};

// Remove a dataProvider prop from an ItemIdentifier
const sanitizeParamForQueryKey = param => {
  const anyParam = param;
  if (anyParam && anyParam.type && anyParam.value && anyParam.dataProvider) {
    return {
      ...anyParam,
      dataProvider: undefined
    };
  } else {
    return param;
  }
};
//# sourceMappingURL=useContentQuery.js.map